import { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { breakpoints, useContainerQuery } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { PopoverDialog as DesignSystemPopoverDialog } from '@frontend/design-system';
import { BGColorMap, BorderColorMap, PromoColorTheme } from './constant';
import { PromoBannerBadge } from './promo-banner-badge';

export type CalloutBannerArrowPosition = 'left' | 'right' | 'top' | 'bottom';

type PopoverDialogProps = Omit<ComponentPropsWithoutRef<typeof DesignSystemPopoverDialog>, 'children'>;

interface CalloutBannerProps {
  color?: PromoColorTheme;
  arrowPosition?: CalloutBannerArrowPosition;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  badge?: ReactNode | 'new' | 'beta';
  containerStyles?: Interpolation<Theme>;
  cardStyles?: Interpolation<Theme>;
  innerRef?: React.Ref<HTMLDivElement>;
  popoverDialogProps?: PopoverDialogProps;
}

const CalloutDialogContent = ({
  badge,
  title,
  description,
  actions,
  cardStyles,
  innerRef,
}: Pick<CalloutBannerProps, 'badge' | 'title' | 'description' | 'actions' | 'cardStyles' | 'innerRef'>) => {
  const [isSmallMode, containerRef] = useContainerQuery({
    maxWidth: breakpoints.small.max,
  });

  return (
    <div
      css={[defaultStyles.contentLayout, isSmallMode && defaultStyles.contentLayoutSmall, cardStyles]}
      ref={composeRefs(innerRef, containerRef) as React.Ref<HTMLDivElement>}
    >
      {!!(title || badge) && (
        <div css={defaultStyles.header}>
          <PromoBannerBadge badgeType={badge} />
          {title}
        </div>
      )}
      {description && <div css={defaultStyles.content}>{description}</div>}
      {actions && <div css={defaultStyles.actionBar}>{actions}</div>}
    </div>
  );
};

export const CalloutDialog = ({
  color = 'seaweed',
  title,
  description,
  actions,
  badge,
  containerStyles,
  cardStyles,
  innerRef,
  popoverDialogProps,
}: CalloutBannerProps) => {
  const popoverCss = popoverDialogProps?.css;
  const mergedArrowProps = {
    fill: BGColorMap[color],
    stroke: BorderColorMap[color],
    strokeWidth: 1,
    ...popoverDialogProps?.arrowProps,
  };

  if (popoverDialogProps) {
    return (
      <DesignSystemPopoverDialog
        {...popoverDialogProps}
        arrowProps={mergedArrowProps}
        css={[defaultStyles.dialogShell(color), containerStyles, popoverCss]}
      >
        <CalloutDialogContent
          badge={badge}
          title={title}
          description={description}
          actions={actions}
          cardStyles={cardStyles}
          innerRef={innerRef}
        />
      </DesignSystemPopoverDialog>
    );
  }

  return (
    <div>
      <div css={[defaultStyles.containerLayout, containerStyles]} ref={innerRef}>
        <div css={defaultStyles.dialogCard(color)}>
          <CalloutDialogContent
            badge={badge}
            title={title}
            description={description}
            actions={actions}
            cardStyles={cardStyles}
          />
        </div>
      </div>
    </div>
  );
};

export const CalloutBanner: FC<CalloutBannerProps> = ({
  color = 'seaweed',
  arrowPosition = 'right',
  title,
  description,
  actions,
  badge,
  containerStyles,
  cardStyles,
  innerRef,
}) => {
  return (
    <div css={[defaultStyles.containerLayout, containerStyles]} ref={innerRef}>
      <div css={[defaultStyles.card(color), defaultStyles.arrow(arrowPosition, color), cardStyles]}>
        {!!(title || badge) && (
          <div css={defaultStyles.header}>
            <PromoBannerBadge badgeType={badge} />
            {title}
          </div>
        )}
        {description && <div css={defaultStyles.content}>{description}</div>}
        {actions && <div css={defaultStyles.actionBar}>{actions}</div>}
      </div>
    </div>
  );
};

const defaultStyles = {
  containerLayout: css`
    display: flex;
    flex-direction: column;
    container-type: inline-size;
  `,
  contentLayout: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1, 1.5)};
  `,
  contentLayoutSmall: css`
    flex-direction: column;
    align-items: stretch;
  `,
  card: (color: PromoColorTheme) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1, 1.5)};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${BGColorMap[color]};
    border: 1px solid ${BorderColorMap[color]};
    box-shadow: ${theme.shadows.floating};

    @container (max-width: ${breakpoints.small.max}px) {
      flex-direction: column;
      align-items: stretch;
    }
  `,
  dialogCard: (color: PromoColorTheme) => css`
    position: relative;
    border-radius: ${theme.borderRadius.medium};
    background-color: ${BGColorMap[color]};
    border: 1px solid ${BorderColorMap[color]};
    box-shadow: ${theme.shadows.floating};
  `,
  dialogShell: (color: PromoColorTheme) => css`
    border-radius: ${theme.borderRadius.medium};
    background-color: ${BGColorMap[color]};
    border: 1px solid ${BorderColorMap[color]};
    box-shadow: ${theme.shadows.floating};
    padding: 0;
  `,
  arrow: (position: CalloutBannerArrowPosition, color: PromoColorTheme) => {
    // Reference: https://cssarrowplease.com/
    switch (position) {
      case 'left':
        return css`
          margin-left: 12px;
          :after,
          :before {
            content: '';
            position: absolute;
            right: 100%;
            top: 50%;
            border: solid transparent;
            height: 0;
            width: 0;
            pointer-events: none;
          }
          :after {
            border-right-color: ${BGColorMap[color]};
            border-width: 12px;
            margin-top: -12px;
          }

          :before {
            border-right-color: ${BorderColorMap[color]};
            border-width: 13px;
            margin-top: -13px;
          }
        `;
      case 'right':
        return css`
          margin-right: 12px;
          :after,
          :before {
            content: '';
            position: absolute;
            left: 100%;
            top: 50%;
            border: solid transparent;
            height: 0;
            width: 0;
            pointer-events: none;
          }

          :after {
            border-left-color: ${BGColorMap[color]};
            border-width: 12px;
            margin-top: -12px;
          }

          :before {
            border-left-color: ${BorderColorMap[color]};
            border-width: 13px;
            margin-top: -13px;
          }
        `;

      case 'top':
        return css`
          margin-top: 12px;
          :after,
          :before {
            content: '';
            position: absolute;
            bottom: 100%;
            left: 50%;
            border: solid transparent;
            height: 0;
            width: 0;
            pointer-events: none;
          }

          :after {
            border-bottom-color: ${BGColorMap[color]};
            border-width: 12px;
            margin-left: -12px;
          }

          :before {
            border-bottom-color: ${BorderColorMap[color]};
            border-width: 13px;
            margin-left: -13px;
          }
        `;
      case 'bottom':
        return css`
          margin-bottom: 12px;
          :after,
          :before {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            border: solid transparent;
            height: 0;
            width: 0;
            pointer-events: none;
          }

          :after {
            border-top-color: ${BGColorMap[color]};
            border-width: 12px;
            margin-left: -12px;
          }

          :before {
            border-top-color: ${BorderColorMap[color]};
            border-width: 13px;
            margin-left: -13px;
          }
        `;
      default:
        return '';
    }
  },
  header: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  content: css`
    display: flex;
    flex-direction: column;
  `,
  actionBar: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    justify-content: flex-end;
  `,
};
